import { Phone, Mail, Facebook, ArrowRight, MapPin } from "lucide-react";
import { SERVICE_AREAS } from "@/lib/mock";
import Logo from "@/components/site/Logo";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="footer"
      className="bg-stone-950 text-stone-300 relative overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-14 border-b border-stone-800">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center bg-stone-50 rounded-2xl p-4">
              <Logo size="xl" />
            </div>
            <p className="mt-8 text-stone-300 leading-relaxed max-w-md text-[15px]">
              Owner-operated. Hand-built. Built to last. Serving the entire
              Greater St. Louis metropolitan area with premium LVP, laminate,
              and tile flooring installation since 2013.
            </p>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-wider text-orange-400 mb-4 font-semibold">
                Follow our work
              </div>
              <a
                href="https://www.facebook.com/profile.php?id=61589883083004"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#1877F2] hover:bg-[#0f5fc7] text-white text-sm font-semibold rounded-full transition-colors shadow-[0_4px_18px_-6px_rgba(24,119,242,0.6)] group"
                aria-label="Follow M&L Exterior and Interior Design on Facebook"
              >
                <Facebook size={16} className="fill-white" />
                Follow on Facebook
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
              <p className="mt-3 text-xs text-stone-500 max-w-xs">
                See our latest projects, before & afters, and customer reviews.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider text-orange-400 mb-5 font-semibold">
              Explore
            </div>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Flooring", href: "/#materials" },
                { label: "All Services", href: "/services" },
                { label: "Portfolio", href: "/#portfolio" },
                { label: "Process", href: "/#process" },
                { label: "Reviews", href: "/#testimonials" },
                { label: "FAQ", href: "/#faq" },
                { label: "Contact", href: "/#contact" },
                { label: "Flooring Contractor · St. Louis", href: "/flooring-contractor-st-louis" },
                { label: "Flooring · O'Fallon, MO", href: "/flooring-installation-ofallon-mo" },
                { label: "Flooring · St. Peters, MO", href: "/flooring-installation-st-peters-mo" },
                { label: "Flooring · St. Charles, MO", href: "/flooring-installation-st-charles-mo" },
                { label: "Flooring · Wentzville, MO", href: "/flooring-installation-wentzville-mo" },
                { label: "Flooring · Chesterfield, MO", href: "/flooring-installation-chesterfield-mo" },
                { label: "Flooring · Kirkwood, MO", href: "/flooring-installation-kirkwood-mo" },
                { label: "Flooring · Ballwin, MO", href: "/flooring-installation-ballwin-mo" },
                { label: "Flooring · Clayton, MO", href: "/flooring-installation-clayton-mo" },
                { label: "Guide · LVP vs Laminate vs Tile", href: "/blog/lvp-vs-laminate-vs-tile-st-louis" },
              ].map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    data-testid={`footer-explore-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="hover:text-orange-400 transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-wider text-orange-400 mb-5 font-semibold">
              Service areas
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {SERVICE_AREAS.map((a) => (
                <li key={a} className="text-stone-300">{a}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider text-orange-400 mb-5 font-semibold">
              Get in touch
            </div>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+16368496681"
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-2 hover:text-orange-400 transition-colors"
                >
                  <Phone size={14} /> 636-849-6681
                </a>
              </li>
              <li>
                <a
                  href="mailto:mlexteriordesign@gmail.com"
                  data-testid="footer-email"
                  className="inline-flex items-start gap-2 hover:text-orange-400 transition-colors break-all"
                >
                  <Mail size={14} className="mt-1 shrink-0" />
                  mlexteriordesign@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-stone-400">
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <address
                  data-testid="footer-address"
                  itemScope
                  itemType="https://schema.org/PostalAddress"
                  className="not-italic leading-relaxed text-[13px]"
                >
                  <span itemProp="streetAddress">2013 Heather Drive</span><br />
                  <span itemProp="addressLocality">O&apos;Fallon</span>,{" "}
                  <span itemProp="addressRegion">MO</span>{" "}
                  <span itemProp="postalCode">63366</span>
                </address>
              </li>
              <li
                data-testid="footer-hours"
                className="text-stone-400 text-[13px] leading-relaxed"
                itemProp="openingHours"
                content="Mo-Su 00:00-23:59"
              >
                <div className="flex items-center gap-2 mb-2 text-stone-300 font-semibold">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>Hours</span>
                </div>
                <dl className="space-y-0.5">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <div
                      key={day}
                      data-testid={`footer-hours-${day.toLowerCase()}`}
                      className="flex justify-between gap-3"
                    >
                      <dt>{day}:</dt>
                      <dd className="text-stone-300">Open 24 hours</dd>
                    </div>
                  ))}
                </dl>
              </li>
            </ul>
            <a
              href="#contact"
              data-testid="footer-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-orange-500 text-stone-50 text-sm font-semibold rounded-full hover:bg-stone-50 hover:text-stone-900 transition-colors group"
            >
              Free Quote
              <ArrowRight
                size={15}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-stone-500">
          <div>© {year} M&amp;L Exterior and Interior Design · All rights reserved · Licensed & Insured</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-stone-200 transition-colors">Privacy</a>
            <a href="#" className="hover:text-stone-200 transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
